import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from "@mui/material";
import { Save as SaveIcon, Lock as LockIcon } from "@mui/icons-material";
import axios from "axios";
import MainLayout from "../../components/layout/MainLayout";

interface ClassModel {
  id: string;
  name: string;
}

interface SectionModel {
  id: string;
  name: string;
}

interface StudentModel {
  id: string;
  firstName: string;
  lastName: string;
  admissionNumber: string;
}

interface AttendanceEntry {
  studentId: string;
  status: "PRESENT" | "ABSENT" | "LATE" | "HALF_DAY";
  remarks: string;
}

const AttendanceCreate: React.FC = () => {
  const [classes, setClasses] = useState<ClassModel[]>([]);
  const [sections, setSections] = useState<SectionModel[]>([]);
  
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  
  const [students, setStudents] = useState<StudentModel[]>([]);
  const [entries, setEntries] = useState<Record<string, AttendanceEntry>>({});
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Load Masters
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/system-settings/classes", {
          params: { isActive: true },
        });
        // Robust handling for array or object wrapper
        const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setClasses(data);
      } catch (e) {
        console.error("Failed to load classes", e);
        setError("Failed to load classes");
      }
    })();
  }, []);

  // Load Sections
  useEffect(() => {
    if (!classId) {
      setSections([]);
      setSectionId("");
      return;
    }
    (async () => {
      try {
        const res = await axios.get("/system-settings/section", {
          params: { classId },
        });
        setSections(res.data);
      } catch {
        setError("Failed to load sections");
      }
    })();
  }, [classId]);

  // Check Attendance & Load Students
  const handleLoad = async () => {
    if (!classId || !sectionId || !date) {
      setError("Please select Class, Section and Date");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setIsLocked(false);
    setStudents([]);
    setEntries({});

    try {
      // 1. Check if attendance already exists
      const attRes = await axios.get("/attendance", {
        params: { classId, sectionId, date },
      });

      if (attRes.data.length === 1) {
        // Attendance exists
        setIsLocked(true);
        const existingAttendance = attRes.data[0];
        
        // Fetch full details to show entries
        const detailRes = await axios.get(`/attendance/${existingAttendance.id}`);
        const existingEntries = detailRes.data.entries;
        
        // Map to students and entries
        const mappedStudents = existingEntries.map((e: any) => e.student);
        const mappedEntries: Record<string, AttendanceEntry> = {};
        existingEntries.forEach((e: any) => {
          mappedEntries[e.studentId] = {
            studentId: e.studentId,
            status: e.status,
            remarks: e.remarks || "",
          };
        });

        setStudents(mappedStudents);
        setEntries(mappedEntries);
        setError(null);
      } else {
        // 2. Fetch Students for new attendance
        const studRes = await axios.get("/students", {
          params: { classId, sectionId, isActive: true },
        });
        const studentList = studRes.data.items || studRes.data;
        setStudents(studentList);

        // Initialize entries
        const initialEntries: Record<string, AttendanceEntry> = {};
        studentList.forEach((s: StudentModel) => {
          initialEntries[s.id] = {
            studentId: s.id,
            status: "PRESENT",
            remarks: "",
          };
        });
        setEntries(initialEntries);
      }
    } catch (err: any) {
      console.error(err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId: string, status: any) => {
    if (isLocked) return;
    setEntries((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], status },
    }));
  };

  const handleRemarksChange = (studentId: string, remarks: string) => {
    if (isLocked) return;
    setEntries((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], remarks },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        classId,
        sectionId,
        date,
        entries: Object.values(entries),
      };
      await axios.post("/attendance/mark", payload);
      setSuccess(true);
      setIsLocked(true);
      // Optional: Refresh or redirect
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PRESENT": return "success";
      case "ABSENT": return "error";
      case "LATE": return "warning";
      case "HALF_DAY": return "info";
      default: return "default";
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 3 }}>
          Mark Attendance
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={classId}
                  label="Class"
                  onChange={(e) => setClassId(e.target.value)}
                >
                  {classes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Section</InputLabel>
                <Select
                  value={sectionId}
                  label="Section"
                  onChange={(e) => setSectionId(e.target.value)}
                  disabled={!classId}
                >
                  {sections.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleLoad}
                disabled={loading || !classId || !sectionId || !date}
                fullWidth
              >
                {loading ? "Loading..." : "Load Students"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity={isLocked ? "info" : "error"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Attendance saved successfully!
          </Alert>
        )}

        {students.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Student List ({students.length})
              </Typography>
              {isLocked ? (
                <Chip icon={<LockIcon />} label="Locked" color="default" />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Attendance"}
                </Button>
              )}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Admission No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => {
                    const entry = entries[student.id] || {};
                    return (
                      <TableRow key={student.id}>
                        <TableCell>{student.admissionNumber}</TableCell>
                        <TableCell>
                          {student.firstName} {student.lastName}
                        </TableCell>
                        <TableCell>
                          {isLocked ? (
                            <Chip 
                              label={entry.status} 
                              color={getStatusColor(entry.status) as any}
                              size="small" 
                            />
                          ) : (
                            <RadioGroup
                              row
                              value={entry.status}
                              onChange={(e) =>
                                handleStatusChange(student.id, e.target.value)
                              }
                            >
                              <FormControlLabel
                                value="PRESENT"
                                control={<Radio size="small" color="success" />}
                                label="P"
                                sx={{ mr: 1 }}
                              />
                              <FormControlLabel
                                value="ABSENT"
                                control={<Radio size="small" color="error" />}
                                label="A"
                                sx={{ mr: 1 }}
                              />
                              <FormControlLabel
                                value="LATE"
                                control={<Radio size="small" color="warning" />}
                                label="L"
                                sx={{ mr: 1 }}
                              />
                              <FormControlLabel
                                value="HALF_DAY"
                                control={<Radio size="small" color="info" />}
                                label="HD"
                                sx={{ mr: 0 }}
                              />
                            </RadioGroup>
                          )}
                        </TableCell>
                        <TableCell>
                          {isLocked ? (
                            entry.remarks || "-"
                          ) : (
                            <TextField
                              size="small"
                              variant="standard"
                              placeholder="Remarks"
                              value={entry.remarks || ""}
                              onChange={(e) =>
                                handleRemarksChange(student.id, e.target.value)
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </MainLayout>
  );
};

export default AttendanceCreate;
